import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  CheckCircleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { HourForm } from "../hours/hour_form";
import { PlanEventCompleteDialog } from "./planEventCompleteDialog";
import { PlanEventProjectSection } from "./planEventProjectSection";
import { formatEventLocalTimeRange } from "../../../pages/dashboard/calendar/utils";
import { fetchProject } from "../../../api/project";
import { PromangRichTextContent } from "../../utils/PromangRichTextContent";

const descriptionForEvent = (event) => {
  const details = event?.module_details || {};
  return (
    details.description ||
    event?.description ||
    details.work_order?.description ||
    details.project_activity?.description ||
    ""
  );
};

export function PlanEventDetailPanel({ event, allDayEvents = [], open, onClose, onUpdated }) {
  const { t } = useTranslation();
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [hourDialogOpen, setHourDialogOpen] = useState(false);
  const [hourProject, setHourProject] = useState(null);

  const details = event?.module_details || {};
  const workOrder = details.work_order;
  const projectActivity = details.project_activity;
  const linkedWorkOrders = details.work_orders?.length
    ? details.work_orders
    : workOrder?.id
      ? [workOrder]
      : [];
  const linkedActivities = details.project_activities?.length
    ? details.project_activities
    : projectActivity?.id
      ? [projectActivity]
      : [];
  const resourceId = details.resource_id;
  const resourceStatus = details.resource_status;
  const projectId = event?.project_id || details.project?.id;
  const plannedHours = details.planned_hours || 0;

  const relatedReport = useMemo(() => {
    if (!projectId || !event?.start_time) return null;
    const dayKey = event.start_time.substring(0, 10);
    return (allDayEvents || []).find(
      (item) =>
        item.module === "report" &&
        String(item?.module_details?.project?.id || item?.project_id) === String(projectId) &&
        item.start_time?.substring(0, 10) === dayKey
    );
  }, [allDayEvents, event, projectId]);

  const hourPrefill = useMemo(() => {
    if (!event) return null;
    const date = event.start_time?.substring(0, 10);
    const project =
      hourProject ||
      (details.project?.displayTitle || details.project?.name ? details.project : null) ||
      (projectId ? { id: projectId } : null);
    const primaryActivity =
      projectActivity ||
      linkedActivities[0] ||
      details.links?.find?.((l) => l.link_type === "project_activity")?.project_activity ||
      null;
    return {
      project_id: projectId,
      project,
      project_activity_id: primaryActivity?.id || details.project_activity?.id,
      project_activity: primaryActivity,
      work_order_id: workOrder?.id,
      date,
      time: plannedHours > 0 ? String(plannedHours) : "",
      user_id: event.user?.id,
      user: event.user,
    };
  }, [event, projectId, projectActivity, workOrder, plannedHours, details, hourProject]);

  useEffect(() => {
    if (!hourDialogOpen || !projectId) {
      setHourProject(null);
      return undefined;
    }
    const fromEvent = details.project;
    if (fromEvent?.displayTitle || fromEvent?.name) {
      setHourProject(fromEvent);
      return undefined;
    }
    let cancelled = false;
    fetchProject(projectId, { fields: "id,displayTitle,name,project_number,customer" })
      .then((res) => {
        if (!cancelled) setHourProject(res?.data || { id: projectId });
      })
      .catch(() => {
        if (!cancelled) setHourProject(fromEvent || { id: projectId });
      });
    return () => {
      cancelled = true;
    };
  }, [hourDialogOpen, projectId, details.project]);

  if (!event) return null;

  return (
    <>
      <PMDialog
        open={open}
        onClose={onClose}
        title={event.title || t("calendar.plan_event.title")}
        size="3xl"
      >
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1 rounded-full bg-teal-100 px-2.5 py-1 text-teal-800">
              {t("project_plan")}
            </span>
            {resourceStatus === "completed" ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-800">
                <CheckCircleIcon className="h-4 w-4" />
                {t("calendar.plan_event.status_completed")}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-1 text-sky-800">
                {t("calendar.plan_event.status_assigned")}
              </span>
            )}
            {event.start_time && event.end_time ? (
              <span className="inline-flex items-center gap-1">
                <ClockIcon className="h-4 w-4" />
                {formatEventLocalTimeRange(event.start_time, event.end_time)}
                {plannedHours > 0 ? ` (${plannedHours}h)` : ""}
              </span>
            ) : null}
          </div>

          {projectId ? (
            <PlanEventProjectSection
              projectId={projectId}
              projectFromEvent={details.project}
            />
          ) : null}

          {descriptionForEvent(event) ? (
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                {t("calendar.plan_event.task_description")}
              </h4>
              <PromangRichTextContent
                html={descriptionForEvent(event)}
                className="rounded-lg bg-gray-50 p-4 text-sm text-gray-700"
              />
            </div>
          ) : null}

          {linkedWorkOrders.map((wo) => (
            <div key={wo.id} className="rounded-lg border border-gray-200 p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                {t("project.event.work_order")}
                {wo.is_primary ? (
                  <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-teal-800">
                    {t("project.plan.primary_link")}
                  </span>
                ) : null}
              </div>
              <div className="text-sm text-gray-600">{wo.displayTitle || wo.title}</div>
              <div className="text-xs text-gray-500">{t("Status")}: {wo.status}</div>
              <Link
                to={`/dashboard/work_orders/${wo.id}`}
                className="text-sm text-blue-600 hover:underline"
              >
                {t("calendar.plan_event.open_work_order")}
              </Link>
            </div>
          ))}

          {linkedActivities.map((pa) => (
            <div key={pa.id} className="rounded-lg border border-gray-200 p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                {t("project_activity.label")}
                {pa.is_primary ? (
                  <span className="rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-teal-800">
                    {t("project.plan.primary_link")}
                  </span>
                ) : null}
              </div>
              <div className="text-sm text-gray-600">{pa.display_title || pa.title}</div>
              <div className="text-xs text-gray-500">{t("Status")}: {pa.status}</div>
              {projectId ? (
                <Link
                  to={`/dashboard/projects/${projectId}?tab=1`}
                  className="text-sm text-blue-600 hover:underline"
                >
                  {t("calendar.plan_event.open_project_activity")}
                </Link>
              ) : null}
            </div>
          ))}

          {relatedReport ? (
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm">
              <Link
                to={`/dashboard/service/reports/${relatedReport.module_id}`}
                className="font-medium text-blue-700 hover:underline"
              >
                {t("calendar.plan_event.related_report")}
              </Link>
            </div>
          ) : null}

          <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:flex-wrap">
            {resourceStatus !== "completed" && resourceId ? (
              <button
                type="button"
                onClick={() => setCompleteDialogOpen(true)}
                className="inline-flex items-center justify-center rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <CheckCircleIcon className="mr-2 h-4 w-4" />
                {t("calendar.plan_event.mark_my_task_complete")}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setHourDialogOpen(true)}
              className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ClockIcon className="mr-2 h-4 w-4" />
              {t("calendar.plan_event.register_hours")}
            </button>
            {projectId ? (
              <Link
                to={`/dashboard/projects/${projectId}/plan`}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {t("calendar.plan_event.open_project_plan")}
              </Link>
            ) : null}
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={hourDialogOpen}
        onClose={() => setHourDialogOpen(false)}
        title={t("calendar.plan_event.register_hours")}
        size="4xl"
      >
        <HourForm
          prefill={hourPrefill}
          showPrefilledContext
          onClose={() => setHourDialogOpen(false)}
        />
      </PMDialog>

      <PlanEventCompleteDialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        linkedWorkOrders={linkedWorkOrders}
        linkedActivities={linkedActivities}
        projectId={projectId}
        resourceId={resourceId}
        onCompleted={() => {
          onUpdated?.();
          onClose?.();
        }}
      />
    </>
  );
}
